/**
 * Generic Game Coach Component
 * 
 * Reusable AI coach panel for any game. Provides:
 * - Get Advice button
 * - Analyze Position button
 * - Chat Q&A
 * - Quick question buttons (game-specific)
 * 
 * Each game passes its own coach service instance and quick questions.
 */

import React, { useState, useRef, useEffect } from 'react';
import { GameCoachService, CoachMessage, GameContext } from '../services/GameCoachService';
import './GameCoach.css';

export interface GameCoachProps {
  coach: GameCoachService;
  gameContext: GameContext | null;
  isPlayerTurn: boolean;
  quickQuestions?: string[];
  accentColor?: string;
  className?: string;
}

export const GameCoach: React.FC<GameCoachProps> = ({
  coach,
  gameContext,
  isPlayerTurn,
  quickQuestions = ['What should I do?', 'Explain my position', 'Any tips?'],
  accentColor = '#6366f1',
  className = ''
}) => {
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `👋 I'm your ${gameContext?.gameName || 'game'} coach! Ask me anything or tap a button below.`,
        timestamp: Date.now()
      }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetAdvice = async () => {
    if (!gameContext || isLoading) return;
    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: '💡 What should I do?', timestamp: Date.now() }]);

    try {
      const advice = await coach.getAdvice(gameContext);
      const emoji = advice.confidence === 'high' ? '🟢' : advice.confidence === 'medium' ? '🟡' : '🔴';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${emoji} **${advice.recommendation}**\n\n${advice.reasoning}${advice.tip ? `\n\n💡 ${advice.tip}` : ''}`,
        timestamp: Date.now()
      }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, try again.', timestamp: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!gameContext || isLoading) return;
    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: '🔍 Analyze position', timestamp: Date.now() }]);

    try {
      const analysis = await coach.analyzePosition(gameContext);
      setMessages(prev => [...prev, { role: 'assistant', content: analysis, timestamp: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Unable to analyze right now.', timestamp: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || !gameContext || isLoading) return;
    const question = inputValue.trim();
    setMessages(prev => [...prev, { role: 'user', content: question, timestamp: Date.now() }]);
    setInputValue('');
    setIsLoading(true);

    try {
      const answer = await coach.askQuestion(question, gameContext);
      setMessages(prev => [...prev, { role: 'assistant', content: answer, timestamp: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, try again.', timestamp: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = async (q: string) => {
    if (!gameContext || isLoading) return;
    setMessages(prev => [...prev, { role: 'user', content: q, timestamp: Date.now() }]);
    setIsLoading(true);

    try {
      const answer = await coach.askQuestion(q, gameContext);
      setMessages(prev => [...prev, { role: 'assistant', content: answer, timestamp: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, try again.', timestamp: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`game-coach ${isExpanded ? 'game-coach--expanded' : ''} ${className}`}>
      <button
        className="game-coach__toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)` }}
      >
        <span className="game-coach__toggle-icon">🧠</span>
        <span className="game-coach__toggle-text">{isExpanded ? 'Hide Coach' : 'AI Coach'}</span>
        {!isExpanded && isPlayerTurn && <span className="game-coach__pulse" />}
      </button>

      {isExpanded && (
        <div className="game-coach__panel">
          <div className="game-coach__actions">
            <button onClick={handleGetAdvice} disabled={isLoading || !gameContext || !isPlayerTurn}>💡 Advice</button>
            <button onClick={handleAnalyze} disabled={isLoading || !gameContext}>🔍 Analyze</button>
          </div>

          <div className="game-coach__messages">
            {messages.map((msg, i) => (
              <div key={i} className={`game-coach__msg game-coach__msg--${msg.role}`}>
                <span dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
              </div>
            ))}
            {isLoading && <div className="game-coach__msg game-coach__msg--assistant"><span className="game-coach__dots"><i/><i/><i/></span></div>}
            <div ref={messagesEndRef} />
          </div>

          <div className="game-coach__quick">
            {quickQuestions.map((q, i) => (
              <button key={i} onClick={() => handleQuickQuestion(q)} disabled={isLoading}>{q}</button>
            ))}
          </div>

          <div className="game-coach__input-row">
            <input
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Ask anything..."
              disabled={isLoading}
            />
            <button onClick={handleSend} disabled={isLoading || !inputValue.trim()}>↑</button>
          </div>

          <div className="game-coach__footer">
            <button onClick={() => { coach.clearHistory(); setMessages([{ role: 'assistant', content: '👋 Chat cleared!', timestamp: Date.now() }]); }}>Clear</button>
            <span>AI advice — use judgment</span>
          </div>
        </div>
      )}
    </div>
  );
};
